/**
 * @file    EnterpriseRoutes.tsx
 * @scope   ENTERPRISE
 * @summary Renders the Enterprise V2 sub-views (Fields / Partition Filters / Filters / Calculations)
 *          selected by the currently active TabNavigator tab.
 * @consumes EnterpriseContext, DiyEnterprise Views, DiyDrawer navigation state
 * @produces JSX for exactly one sub-view at a time (or nothing on the main tab)
 * @sideEffects Writes to the EnterpriseContext and to the drawer navigation state via callbacks.
 * @notes Pure routing/presentation: it owns no state of its own, so it can be mounted anywhere
 *        inside the drawer without changing behaviour.
 */
import React from 'react';
import FieldsManager, {
  FieldsManagerTab,
} from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/FieldsManager/FieldsManager';
import CalculationsSettingsManager, {
  CalculationsSettingsManagerTab,
} from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/CalculationsSettingsManager/CalculationsSettingsManager';
import FiltersSettingsManager, {
  FiltersSettingsManagerTab,
} from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/FiltersSettingsManager/FiltersSettingsManager';
import PartitionFiltersManager, {
  PartitionFiltersManagerTab,
} from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/PartitionFiltersManager/PartitionFiltersManager';
import { getSelectedUserFields } from 'components/DiyWidgetEngine/Subtypes/Enterprise/utils/mapping/fieldMapping';
import { useEnterpriseContext } from 'components/DiyWidgetEngine/Subtypes/Enterprise/State/EnterpriseContext';
import { useEnterpriseNavigation } from '../plugins/enterprise/EnterpriseNavigationContext';
import { EngineRoutesProps } from '../plugins/types';

// ⚡ ENTERPRISE V2 — NEW WORLD. Do not use for legacy charts.

const EnterpriseRoutes: React.FC<EngineRoutesProps> = ({
  currentTab,
  setCurrentTab,
  goToMainTab,
  selectedPartition,
  endpoint,
}) => {
  const {
    advancedSettingsContext,
    setAdvancedSettingsContext,
    filtersSettingsContext,
    setFiltersSettingsContext,
    partitionFiltersContext,
    setPartitionFiltersContext,
  } = useEnterpriseNavigation();
  const {
    state: enterpriseGlobalObj,
    updateUserFields,
    setViewOptions,
    updateFieldInPartition,
    getAdvancedSettingsField,
  } = useEnterpriseContext();

  /** View options row matching the partition currently under edition. */
  const viewOptionsForSelectedPartition =
    enterpriseGlobalObj.options.availableFieldsManagerOptions.find(
      (opt) => opt.parentPartition?.value === selectedPartition?.value
    );

  /** Filter fields offered by the selected partition (empty when none is selected). */
  const availableFilterFields = viewOptionsForSelectedPartition?.FiltersOptions ?? [];

  /** Fields of the selected partition, or `null` when no partition is selected. */
  const selectedUserFields = selectedPartition
    ? getSelectedUserFields(
        selectedPartition,
        enterpriseGlobalObj.selectedValues.selectedEnterpriseFields
      )
    : null;

  const advancedSettingsField = getAdvancedSettingsField(
    advancedSettingsContext?.partitionGuid,
    advancedSettingsContext?.fieldIndex
  );

  const filtersSettingsField = getAdvancedSettingsField(
    filtersSettingsContext?.partitionGuid,
    filtersSettingsContext?.fieldIndex
  );

  /** Opens the per-field calculations screen for `fieldIndex`. */
  const openCalculationsSettings = (_field: any, fieldIndex: number) => {
    setAdvancedSettingsContext({ fieldIndex, partitionGuid: selectedPartition?.value ?? '' });
    setCurrentTab(CalculationsSettingsManagerTab);
  };

  /** Opens the per-field filters screen, optionally focusing an existing filter. */
  const openFiltersSettings = (fieldIndex: number, filterId: any) => {
    setFiltersSettingsContext({
      fieldIndex,
      partitionGuid: selectedPartition?.value ?? '',
      focusedFilterId: filterId,
    });
    setCurrentTab(FiltersSettingsManagerTab);
  };

  /** Opens the partition-wide filters screen. */
  const openPartitionFilters = () => {
    setPartitionFiltersContext({
      partitionGuid: selectedPartition?.value ?? '',
      focusedFilterId: null,
    });
    setCurrentTab(PartitionFiltersManagerTab);
  };

  /** Every sub-view returns to the FieldsManager. */
  const backToFieldsManager = () => setCurrentTab(FieldsManagerTab);

  return (
    <>
      {/* Manage Fields View */}
      {currentTab.name === FieldsManagerTab.name && selectedPartition && (
        <FieldsManager
          onBack={goToMainTab}
          selectedUserFields={getSelectedUserFields(
            selectedPartition,
            enterpriseGlobalObj.selectedValues.selectedEnterpriseFields
          )}
          onSelectedUserFieldsChange={updateUserFields}
          fieldsManagerViewOptions={viewOptionsForSelectedPartition}
          onViewOptionsLoaded={setViewOptions}
          endpoint={endpoint}
          selectedPartition={selectedPartition}
          expandedIndex={
            advancedSettingsContext?.fieldIndex ?? filtersSettingsContext?.fieldIndex ?? null
          }
          onAdvancedSettings={openCalculationsSettings}
          onEditFilter={openFiltersSettings}
          onManagePartitionFilters={openPartitionFilters}
          defaultActiveTab={filtersSettingsContext != null ? 'filters' : undefined}
        />
      )}

      {/* Partition Filters Settings View */}
      {currentTab.name === PartitionFiltersManagerTab.name && (
        <PartitionFiltersManager
          onBack={backToFieldsManager}
          selectedUserFields={selectedUserFields}
          dataSourceDisplayName={viewOptionsForSelectedPartition?.parentPartition?.label}
          onUserFieldsChange={updateUserFields}
          focusedFilterId={partitionFiltersContext?.focusedFilterId}
          availableFilterFields={availableFilterFields}
        />
      )}

      {/* Filters Settings View */}
      {currentTab.name === FiltersSettingsManagerTab.name && (
        <FiltersSettingsManager
          onBack={backToFieldsManager}
          selectedField={filtersSettingsField}
          onFieldChange={(updatedField) =>
            filtersSettingsContext &&
            updateFieldInPartition(
              filtersSettingsContext.partitionGuid,
              filtersSettingsContext.fieldIndex,
              updatedField
            )
          }
          focusedFilterId={filtersSettingsContext?.focusedFilterId}
          availableFilterFields={availableFilterFields}
        />
      )}

      {/* Calculations Settings View */}
      {currentTab.name === CalculationsSettingsManagerTab.name && (
        <CalculationsSettingsManager
          onBack={backToFieldsManager}
          selectedField={advancedSettingsField}
          onFieldChange={(updatedField) =>
            advancedSettingsContext &&
            updateFieldInPartition(
              advancedSettingsContext.partitionGuid,
              advancedSettingsContext.fieldIndex,
              updatedField
            )
          }
        />
      )}
    </>
  );
};

export default EnterpriseRoutes;
